import React, { useState } from 'react';
import api from '../api';
import { useTranslation } from '../LanguageContext';

/**
 * Momentum, not a total.
 *
 * The ledger below this card already says what has been earned. What it never
 * said is whether that is *going well* — which is the only question a creator
 * actually opens the finance page to answer. Three things do that job here:
 * this month against the same slice of last month, where the month is heading
 * at the current rate, and how far that is from the target the creator set.
 *
 * The comparison is like-for-like on purpose. `pace.change_percent` comes from
 * last month **to the same day**, not from the whole of last month, because on
 * the 3rd of a month a full-month comparison reports a catastrophe every time.
 * The full-month figure is still shown, just as a reference line rather than
 * as the percentage.
 */

const usd = (value) => `$${Number(value || 0).toLocaleString('en-US', {
    minimumFractionDigits: 2, maximumFractionDigits: 2,
})}`;

const usdShort = (value) => {
    const n = Number(value || 0);
    if (n >= 1000) return `$${(n / 1000).toFixed(n % 1000 === 0 ? 0 : 1)}k`;
    return `$${n.toFixed(0)}`;
};

/** Up, down or new — never a bare "+0%" for an account with no history. */
function DeltaPill({ percent, t }) {
    if (percent === null || percent === undefined) {
        return (
            <span className="inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-bold bg-slate-100 text-slate-500 border border-slate-200">
                {t('finance.goalNoBaseline')}
            </span>
        );
    }
    const up = percent >= 0;
    return (
        <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-bold border ${up
            ? 'bg-emerald-50 text-emerald-700 border-emerald-200'
            : 'bg-red-50 text-red-600 border-red-200'}`}>
            <svg xmlns="http://www.w3.org/2000/svg" className={`w-3 h-3 ${up ? '' : 'rotate-180'}`}
                fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={3}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M5 15l7-7 7 7" />
            </svg>
            {up ? '+' : ''}{Math.round(percent)}%
        </span>
    );
}

function GoalEditor({ current, onSaved, onCancel, t }) {
    const [value, setValue] = useState(current > 0 ? String(current) : '');
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState('');

    const save = async (amount) => {
        setBusy(true);
        setError('');
        try {
            const res = await api.post('/api/finance/goal/', { target_usd: amount });
            if (res.status === 200) onSaved(res.data);
        } catch (err) {
            setError(t('finance.goalSaveError'));
        } finally { setBusy(false); }
    };

    return (
        <div className="space-y-2">
            <div className="flex items-center gap-2">
                <div className="relative flex-1">
                    <span className="absolute left-3 top-1/2 -translate-y-1/2 text-sm font-bold text-slate-400 pointer-events-none">$</span>
                    <input type="number" min={0} step="50" value={value} autoFocus
                        placeholder={t('finance.goalPlaceholder')}
                        onChange={(e) => setValue(e.target.value)}
                        onKeyDown={(e) => { if (e.key === 'Enter') save(Number(value || 0)); }}
                        className="input py-2 pl-7 text-sm w-full" />
                </div>
                <button onClick={() => save(Number(value || 0))} disabled={busy}
                    className="btn-primary px-4 py-2 text-sm flex items-center gap-2">
                    {busy && <span className="spinner-sm" />}
                    {t('finance.goalSave')}
                </button>
            </div>
            <div className="flex items-center gap-2">
                <button onClick={onCancel} className="text-[11px] font-semibold text-slate-500 hover:text-slate-700">
                    {t('campaigns.cancel')}
                </button>
                {current > 0 && (
                    /* Clearing is posting zero — the same call, so the card can
                       never end up showing a target the server does not hold. */
                    <button onClick={() => save(0)}
                        className="text-[11px] font-semibold text-red-500 hover:text-red-600 ml-auto">
                        {t('finance.goalClear')}
                    </button>
                )}
            </div>
            {error && <p className="text-[11px] text-red-600">{error}</p>}
        </div>
    );
}

function EarningsGoalCard({ pace: initial, className = '' }) {
    const { t } = useTranslation();
    const [pace, setPace] = useState(initial);
    const [editing, setEditing] = useState(false);

    if (!pace) return null;

    const goal = pace.goal || {};
    const progress = Math.min(100, Math.max(0, goal.progress_percent || 0));
    // Where the month *should* be by today if the target is to be met — the
    // marker is what turns a bar into a pace, rather than just a total.
    const expected = pace.days_in_month
        ? Math.min(100, (pace.days_elapsed / pace.days_in_month) * 100)
        : 0;

    return (
        <section className={`rounded-2xl border border-slate-200 bg-white p-4 sm:p-5 ${className}`}>
            <div className="flex items-start justify-between gap-3 mb-1">
                <h2 className="text-lg font-bold text-slate-800">{t('finance.goalTitle')}</h2>
                <span className="text-[10px] font-bold text-slate-400 tabular-nums flex-shrink-0 mt-1.5">
                    {pace.month}
                </span>
            </div>
            <p className="text-xs text-slate-400 mb-4">{t('finance.goalHint')}</p>

            {/* This month */}
            <div className="flex items-end gap-3 flex-wrap">
                <div>
                    <p className="text-3xl font-black text-slate-900 tracking-tight tabular-nums">
                        {usd(pace.this_month_usd)}
                    </p>
                    <p className="text-[11px] text-slate-400 mt-0.5">{t('finance.goalThisMonth')}</p>
                </div>
                <div className="pb-1">
                    <DeltaPill percent={pace.change_percent} t={t} />
                </div>
            </div>

            {/* The two reference figures behind that percentage. Stated
                explicitly because "vs last month" is ambiguous and the
                ambiguity is exactly where the number stops being trusted. */}
            <div className="grid grid-cols-2 gap-2 mt-3">
                <div className="rounded-xl bg-slate-50 border border-slate-100 p-2.5">
                    <p className="text-sm font-black text-slate-700 tabular-nums">
                        {usd(pace.last_month_to_date_usd)}
                    </p>
                    <p className="text-[10px] text-slate-500 font-semibold leading-tight mt-0.5">
                        {t('finance.goalLastMonthToDate')}
                    </p>
                </div>
                <div className="rounded-xl bg-slate-50 border border-slate-100 p-2.5">
                    <p className="text-sm font-black text-slate-700 tabular-nums">
                        {usd(pace.last_month_usd)}
                    </p>
                    <p className="text-[10px] text-slate-500 font-semibold leading-tight mt-0.5">
                        {t('finance.goalLastMonthFull')}
                    </p>
                </div>
            </div>

            {/* Projection — labelled an estimate everywhere it appears, because
                income arrives in lumps and a straight-line run rate over lumpy
                income is a guess, not a forecast. */}
            <div className="mt-3 rounded-xl bg-primary-50 border border-primary-100 p-3 flex items-center gap-3">
                <svg xmlns="http://www.w3.org/2000/svg" className="w-5 h-5 text-primary-600 flex-shrink-0"
                    fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M13 7h8m0 0v8m0-8l-8 8-4-4-6 6" />
                </svg>
                <div className="min-w-0 flex-1">
                    <p className="text-sm font-black text-primary-900 tabular-nums">
                        {usd(pace.projection_usd)}
                    </p>
                    <p className="text-[10px] text-primary-700/80 leading-tight">
                        {t('finance.goalProjection')} · {t('finance.goalEstimate')}
                    </p>
                </div>
                {pace.incoming_escrow_usd > 0 && (
                    <div className="text-right flex-shrink-0">
                        <p className="text-sm font-black text-slate-700 tabular-nums">
                            {usdShort(pace.incoming_escrow_usd)}
                        </p>
                        <p className="text-[10px] text-slate-500 leading-tight">{t('finance.goalInEscrow')}</p>
                    </div>
                )}
            </div>

            {/* Target */}
            <div className="mt-4 pt-4 border-t border-slate-100">
                {editing ? (
                    <GoalEditor current={goal.target_usd} t={t}
                        onCancel={() => setEditing(false)}
                        onSaved={(data) => { setPace(data); setEditing(false); }} />
                ) : goal.is_set ? (
                    <>
                        <div className="flex items-center justify-between gap-2 mb-2">
                            <span className="text-xs font-bold text-slate-600">
                                {t('finance.goalTarget')}: <span className="tabular-nums">{usd(goal.target_usd)}</span>
                            </span>
                            <div className="flex items-center gap-2">
                                <span className={`px-2 py-0.5 rounded-full text-[10px] font-bold border ${goal.on_track
                                    ? 'bg-emerald-50 text-emerald-700 border-emerald-200'
                                    : 'bg-amber-50 text-amber-700 border-amber-200'}`}>
                                    {goal.on_track ? t('finance.goalOnTrack') : t('finance.goalBehind')}
                                </span>
                                <button onClick={() => setEditing(true)}
                                    className="text-[11px] font-semibold text-primary-700 hover:underline">
                                    {t('campaigns.edit')}
                                </button>
                            </div>
                        </div>

                        <div className="relative h-2.5 rounded-full bg-slate-100 overflow-hidden">
                            <div className={`h-full rounded-full transition-all duration-500 ${goal.on_track ? 'bg-emerald-500' : 'bg-primary-500'}`}
                                style={{ width: `${progress}%` }} />
                            {/* Pace marker: where the bar would be if the month
                                were on schedule today. Without it a half-full
                                bar on the 28th looks the same as one on the 5th. */}
                            {expected > 0 && expected < 100 && (
                                <div className="absolute top-0 bottom-0 w-0.5 bg-slate-400"
                                    style={{ left: `${expected}%` }}
                                    title={t('finance.goalPaceMarker')} />
                            )}
                        </div>

                        <div className="flex items-center justify-between gap-2 mt-1.5 text-[11px] text-slate-500">
                            <span className="tabular-nums">%{Math.round(progress)}</span>
                            <span>
                                {goal.remaining_usd > 0
                                    ? <>{t('finance.goalRemaining')} <strong className="text-slate-700 tabular-nums">{usd(goal.remaining_usd)}</strong></>
                                    : <strong className="text-emerald-600">{t('finance.goalReached')}</strong>}
                            </span>
                        </div>

                        {goal.remaining_usd > 0 && pace.days_left > 0 && (
                            <p className="text-[11px] text-slate-400 mt-1.5 leading-relaxed">
                                {t('finance.goalDailyNeeded')}{' '}
                                <strong className="text-slate-600 tabular-nums">{usd(goal.daily_needed_usd)}</strong>
                                {' · '}{pace.days_left} {t('finance.goalDaysLeft')}
                            </p>
                        )}
                    </>
                ) : (
                    <button onClick={() => setEditing(true)}
                        className="w-full py-2.5 rounded-xl text-sm font-bold text-primary-700 bg-primary-50 border border-primary-200 hover:bg-primary-100 transition">
                        + {t('finance.goalSet')}
                    </button>
                )}
            </div>
        </section>
    );
}

export default EarningsGoalCard;
